import { z } from "zod";

/**
 * M3 — trailing-7-day median wait to match, one point per day, human and bot
 * side by side. Mirrors `oracle.sql`'s rolling-wait section row for row.
 *
 * **p50 only.** No audit computed a p90 for the rolling series, so none is
 * returned: the same `percentile_cont` would produce a real number, but a
 * figure nothing verifies is indistinguishable from a wrong one on a dashboard
 * whose whole purpose is catching wrong numbers. The oracle-backed p90 lives on
 * `matchmaking/wait-cdf`, where it is checkable.
 *
 * A day with no anchor on one side reports `null` for that side's median, never
 * `0` — a zero there would read as an instant match. `nHuman7d` / `nBot7d` say
 * which case it is.
 *
 * Daily grain only, and the same `from − 6 days` opening as route 23.
 *
 * `feeGuardViolations` is surfaced, never swallowed: buckets are keyed on
 * `min(fee)` across the proposal's seats, which must equal `matchedFeeUsdt` on
 * every non-null row. A non-zero count means a writer diverged from that rule.
 * It covers the whole SCANNED window, wider than the trimmed output — the guard
 * is a tripwire, so over-reporting is the safe direction.
 */
export const ArenaOpsMatchmakingWaitSeriesResponseSchema = z.object({
  points: z.array(
    z.object({
      /** UTC midnight of the day this trailing window ENDS on. */
      bucketStart: z.iso.datetime(),
      /** Seat-0 anchors matched against a human, over the trailing 7 days. */
      nHuman7d: z.number().int(),
      /** Seat-0 anchors matched against a bot, over the trailing 7 days. */
      nBot7d: z.number().int(),
      /** Median wait against a human. Null when `nHuman7d` is 0. */
      p50HumanSeconds: z.number().nullable(),
      /** Median wait against a bot. Null when `nBot7d` is 0. */
      p50BotSeconds: z.number().nullable(),
    }),
  ),
  feeGuardViolations: z.number().int(),
});
export type ArenaOpsMatchmakingWaitSeriesResponse = z.infer<typeof ArenaOpsMatchmakingWaitSeriesResponseSchema>;
